import React, { useState } from 'react';
import { ArrowRight, ShieldCheck } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import { countryCodes } from '../data/mockData';

const HeroBanner = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({ name: '', email: '', countryCode: '+1', phone: '', description: '' });
  const [errors, setErrors] = useState({ email: '', phone: '' });

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const handleEmailChange = (value) => {
    setFormData({ ...formData, email: value });
    setErrors({ ...errors, email: value && !validateEmail(value) ? 'Please enter a valid email' : '' });
  };
  const handlePhoneChange = (value) => {
    const d = value.replace(/\D/g, '');
    if (d.length <= 10) {
      setFormData({ ...formData, phone: d });
      setErrors({ ...errors, phone: d && d.length !== 10 ? 'Must be 10 digits' : '' });
    }
  };
  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = { email: '', phone: '' };
    let hasErrors = false;
    if (!validateEmail(formData.email)) { newErrors.email = 'Please enter a valid email'; hasErrors = true; }
    if (formData.phone.length !== 10) { newErrors.phone = 'Must be 10 digits'; hasErrors = true; }
    if (hasErrors) { setErrors(newErrors); return; }
    toast({ title: "We're On It!", description: "A trademark specialist will reach out within 24 hours." });
    setFormData({ name: '', email: '', countryCode: '+1', phone: '', description: '' });
    setErrors({ email: '', phone: '' });
  };

  return (
    <section id="home" data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden bg-[#041B1C]">
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1634976276568-9ea10353a8cd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwyfHxkYXJrJTIwbHV4dXJ5JTIwYWJzdHJhY3QlMjBzbW9rZSUyMHNpbGslMjB0ZXh0dXJlfGVufDB8fHxncmVlbnwxNzc2MjkyOTMwfDA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#041B1C]/30 via-transparent to-[#041B1C]/80" />
        <div className="absolute top-[-20%] right-[-10%] w-[600px] h-[600px] rounded-full bg-[#A5BF23]/5 blur-[150px]" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[400px] h-[400px] rounded-full bg-[#093336]/80 blur-[120px]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10 pt-28 pb-20 w-full">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-7 space-y-8">
            <div className="inline-flex items-center gap-2 bg-[#A5BF23]/10 border border-[#A5BF23]/20 px-4 py-2 rounded-full">
              <ShieldCheck className="w-4 h-4 text-[#A5BF23]" />
              <span className="text-xs font-medium text-[#A5BF23] tracking-wide uppercase">Protecting 10,000+ Brand Names Nationwide</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] text-[#F0F4F4]" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Protect Your
              <br />
              Brand Name.
              <br />
              <span className="text-[#A5BF23]">Before Someone Else Takes It.</span>
            </h1>

            <p className="text-base md:text-lg text-[#A0B2B2] leading-relaxed max-w-xl">
              Your brand name is the foundation of your business identity. Our licensed attorneys handle complete brand name trademark registration with the USPTO, from initial search to federal certification. Plans start at <span className="text-[#A5BF23] font-semibold">$49</span>.
            </p>

            <div className="grid sm:grid-cols-3 gap-4 max-w-xl">
              {[
                { num: '50', label: 'State Protection' },
                { num: '48h', label: 'Filing Speed' },
                { num: '99%', label: 'Success Rate' },
              ].map((s, i) => (
                <div key={i} className="bg-[#093336]/60 border border-white/5 rounded-2xl p-4 text-center backdrop-blur-sm">
                  <div className="text-2xl font-bold text-[#A5BF23]" style={{ fontFamily: "'Outfit', sans-serif" }}>{s.num}</div>
                  <div className="text-xs text-[#A0B2B2] mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <div data-testid="hero-contact-form" className="bg-[#093336]/60 backdrop-blur-xl border border-white/10 rounded-2xl p-7 relative">
              <div className="absolute -top-3 left-6">
                <span className="bg-[#A5BF23] text-[#041B1C] px-4 py-1.5 rounded-full text-xs font-bold tracking-wide">STARTING AT $49</span>
              </div>
              <div className="mb-5 mt-2">
                <h3 className="text-xl font-bold text-[#F0F4F4]" style={{ fontFamily: "'Outfit', sans-serif" }}>Trademark Your Brand Name</h3>
                <p className="text-sm text-[#A0B2B2] mt-1">Secure federal rights to your brand name with the USPTO.</p>
              </div>
              <form onSubmit={handleSubmit} className="space-y-3">
                <div>
                  <Label htmlFor="hero-name" className="text-[#A0B2B2] text-xs font-medium">Full Name *</Label>
                  <Input id="hero-name" data-testid="hero-name-input" placeholder="Enter your full name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required className="h-10 bg-[#041B1C] border-white/10 text-[#F0F4F4] placeholder:text-[#A0B2B2]/50 focus:border-[#A5BF23] focus:ring-1 focus:ring-[#A5BF23] rounded-xl mt-1" />
                </div>
                <div>
                  <Label htmlFor="hero-email" className="text-[#A0B2B2] text-xs font-medium">Email *</Label>
                  <Input id="hero-email" data-testid="hero-email-input" type="email" placeholder="your@email.com" value={formData.email} onChange={(e) => handleEmailChange(e.target.value)} required className={`h-10 bg-[#041B1C] text-[#F0F4F4] placeholder:text-[#A0B2B2]/50 rounded-xl mt-1 ${errors.email ? 'border-red-500' : 'border-white/10'} focus:border-[#A5BF23] focus:ring-1 focus:ring-[#A5BF23]`} />
                  {errors.email && <p className="text-red-400 text-xs mt-0.5">{errors.email}</p>}
                </div>
                <div>
                  <Label htmlFor="hero-phone" className="text-[#A0B2B2] text-xs font-medium">Phone *</Label>
                  <div className="flex gap-2 mt-1">
                    <Select value={formData.countryCode} onValueChange={(v) => setFormData({ ...formData, countryCode: v })}>
                      <SelectTrigger data-testid="hero-country-select" className="w-[100px] h-10 bg-[#041B1C] border-white/10 text-[#F0F4F4] rounded-xl"><SelectValue /></SelectTrigger>
                      <SelectContent className="max-h-[300px] bg-[#093336] border-white/10">
                        {countryCodes.map((item) => (<SelectItem key={item.code} value={item.code} className="text-[#F0F4F4]"><span className="flex items-center gap-2"><span>{item.flag}</span><span>{item.code}</span></span></SelectItem>))}
                      </SelectContent>
                    </Select>
                    <Input id="hero-phone" data-testid="hero-phone-input" type="tel" placeholder="1234567890" value={formData.phone} onChange={(e) => handlePhoneChange(e.target.value)} required maxLength={10} className={`flex-1 h-10 bg-[#041B1C] text-[#F0F4F4] placeholder:text-[#A0B2B2]/50 rounded-xl ${errors.phone ? 'border-red-500' : 'border-white/10'} focus:border-[#A5BF23] focus:ring-1 focus:ring-[#A5BF23]`} />
                  </div>
                  {errors.phone && <p className="text-red-400 text-xs mt-0.5">{errors.phone}</p>}
                </div>
                <div>
                  <Label htmlFor="hero-desc" className="text-[#A0B2B2] text-xs font-medium">What do you need?</Label>
                  <Textarea id="hero-desc" data-testid="hero-description-input" placeholder="e.g. Register my brand name and logo" rows={2} value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="bg-[#041B1C] border-white/10 text-[#F0F4F4] placeholder:text-[#A0B2B2]/50 focus:border-[#A5BF23] focus:ring-1 focus:ring-[#A5BF23] resize-none rounded-xl mt-1" />
                </div>
                <Button type="submit" data-testid="hero-contact-submit" className="w-full bg-[#A5BF23] hover:bg-[#8A9F1D] text-[#041B1C] h-11 text-sm font-semibold rounded-full transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_8px_32px_rgba(165,191,35,0.3)]">
                  Get My Free Consultation <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
                <p className="text-[10px] text-[#A0B2B2]/60 text-center">By submitting, you agree to our Terms and Privacy Policy.</p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroBanner;
